<template>
  <div>
    <b-card no-body class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title">
          <h3 class="card-label">
            {{ $t("MENU.ITEM.EXP.AI_COMPANIES") }}
            <i class="mr-2"></i>
            <small class="">{{ $t("LABELS.LIST") }}</small>
          </h3>
        </div>
        <div class="card-toolbar">
          <b-button
            variant="success"
            class="font-weight-bolder"
            @click="showAddModal"
          >
            <i class="flaticon2-plus"></i>
            {{ $t("LABELS.ADD_NEW") }}
          </b-button>
        </div>
      </template>
      <v-card v-show="!isPageLoading">
        <SearchTools
          :searchQuery.sync="searchQuery"
          placeholder="Търсене по име, Булстат, ДДС номер, МОЛ, адрес"
          @do-search="doSearch"
          @do-clear="doClear"
        />
        <v-data-table
          :headers="tableHeaders"
          :items="items"
          :options.sync="options"
          :items-per-page="options.itemsPerPage"
          :server-items-length="totalItems"
          :loading="isLoadingAiCompany"
          loading-text="Loading... Please wait"
          class="px-6 elevation-1"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right'
          }"
          @update:options="getDataFromApi()"
        >
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              class="mr-2"
              fab
              dark
              x-small
              @click="showEditModal(item.id)"
            >
              <v-icon dark>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <template v-slot:[`item.name`]="{ item }">
            <span :class="item.isConfirmed ? '' : 'text-danger'">
              {{ item.name }}
            </span>
          </template>
          <template v-slot:no-results>
            <v-alert :value="true" color="error" icon="warning">
              {{ $t("LABELS.NO_RESULTS") }}
            </v-alert>
          </template>
        </v-data-table>
      </v-card>
      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      ></v-skeleton-loader>
    </b-card>

    <b-modal
      ref="editModal"
      :title="modalTitle"
      @ok="saveCompany"
      @hidden="resetModal"
      ok-variant="success"
      :ok-title="$t('LABELS.SAVE_CHANGES')"
      :cancel-title="$t('LABELS.CANCEL')"
      size="lg"
      hide-footer
    >
      <div v-if="modalLoading" class="text-center my-4">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </div>
      <div v-else>
        <AiCompanyForm
          v-if="modalForm"
          :form="modalForm"
          :errors="modalError ? [modalError] : []"
          ref="companyForm"
        />
        <v-alert v-if="modalError" type="error" class="mt-3">
          {{ modalError }}
        </v-alert>
        <div class="d-flex justify-content-end mt-4">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="$refs.editModal.hide()"
          >
            {{ $t("LABELS.CANCEL") }}
          </b-button>
          <b-button variant="success" @click="saveCompany">
            {{ $t("LABELS.SAVE_CHANGES") }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  FETCH_AI_COMPANIES,
  FETCH_AI_COMPANY,
  SAVE_AI_COMPANY,
  UPDATE_AI_COMPANY
} from "../store/ai-companies.module";
import SearchTools from "@/core/components/table/SearchTools.vue";
import AiCompanyForm from "../components/AiCompanyForm.vue";

export default {
  components: {
    SearchTools,
    AiCompanyForm
  },
  data() {
    return {
      isPageLoading: true,
      items: [],
      totalItems: 100,
      searchQuery: "",
      options: {
        page: 1,
        itemsPerPage: 30,
        totalItems: 100
      },
      rowsPerPageItems: [10, 20, 30, 40, 50],
      headers: [
        { text: this.$t("FORM_LABELS.NAME"), value: "name" },
        { text: this.$t("FORM_LABELS.BULSTAT"), value: "bulstat" },
        { text: this.$t("FORM_LABELS.VAT_NUMBER"), value: "vatNumber" },
        { text: this.$t("FORM_LABELS.MOL_NUMBER"), value: "molNumber" },
        { text: this.$t("FORM_LABELS.ADDRESS"), value: "address" },
        {
          text: this.$t("MENU.ITEM.ACTIONS"),
          value: "actions",
          sortable: false
        }
      ],
      modalForm: null,
      modalError: "",
      modalLoading: false,
      modalEditId: null,
      modalTitle: ""
    };
  },
  computed: {
    ...mapGetters(["isLoadingAiCompany"]),
    tableHeaders() {
      return this.headers;
    },
    params() {
      return {
        ...this.options,
        query: this.searchQuery
      };
    }
  },
  mounted() {
    this.getDataFromApi();
  },
  methods: {
    doSearch() {
      if (this.options.page == 1) {
        this.getDataFromApi();
      } else {
        this.options.page = 1;
      }
    },
    doClear() {
      this.searchQuery = "";
      if (this.options.page == 1) {
        this.getDataFromApi();
      } else {
        this.options.page = 1;
      }
    },
    getDataFromApi() {
      let params = { ...this.params };
      params.length = params.itemsPerPage;
      params.start =
        params.page == 1 ? 0 : params.itemsPerPage * (params.page - 1);

      let apiParams = this.transformUrlParams(params);

      this.$store.dispatch(FETCH_AI_COMPANIES, apiParams).then(data => {
        this.items = data.data.items;
        this.totalItems = data.data.totalItemsCount;
        this.isPageLoading = false;
      });
    },
    transformUrlParams(params) {
      return (
        "?" +
        Object.keys(params)
          .map(key => key + "=" + encodeURIComponent(params[key]))
          .join("&")
      );
    },
    showEditModal(id) {
      this.modalTitle = this.$t("LABELS.EDIT_COMPANY");
      this.modalLoading = true;
      this.modalError = "";
      this.modalEditId = id;
      this.$refs.editModal.show();
      this.$store
        .dispatch(FETCH_AI_COMPANY, id)
        .then(data => {
          const company = { ...data.data };
          company.isConfirmed = Boolean(company.isConfirmed);
          company.isBuyer = Boolean(company.isBuyer);
          company.isSupplier = Boolean(company.isSupplier);
          this.modalForm = company;
          this.modalLoading = false;
        })
        .catch(() => {
          this.modalError = this.$t("LABELS.ERROR_LOADING_COMPANY");
          this.modalLoading = false;
        });
    },
    showAddModal() {
      this.modalTitle = this.$t("LABELS.ADD_NEW_COMPANY");
      this.modalForm = {
        name: "",
        bulstat: "",
        vatNumber: "",
        molNumber: "",
        address: "",
        isConfirmed: false,
        isBuyer: false,
        isSupplier: false
      };
      this.modalEditId = null;
      this.modalError = "";
      this.modalLoading = false;
      this.$refs.editModal.show();
    },
    async saveCompany() {
      this.modalError = "";
      this.modalLoading = true;
      try {
        if (this.modalEditId) {
          await this.$store.dispatch(UPDATE_AI_COMPANY, {
            id: this.modalEditId,
            payload: this.modalForm
          });
        } else {
          await this.$store.dispatch(SAVE_AI_COMPANY, this.modalForm);
        }
        this.getDataFromApi();
        this.$refs.editModal.hide();
      } catch (error) {
        this.modalError =
          error?.data?.message || this.$t("LABELS.ERROR_SAVING_COMPANY");
      } finally {
        this.modalLoading = false;
      }
    },
    resetModal() {
      this.modalForm = null;
      this.modalEditId = null;
      this.modalError = "";
      this.modalLoading = false;
      this.modalTitle = "";
    }
  }
};
</script>
